import { FC, useState } from 'react'
import { ReactComponent as InfoIcon } from 'src/assets/svg/ri-information-fill.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { ReactComponent as TripadvisorIcon } from 'src/assets/svg/tripadvisor.svg'
import { ReactComponent as WarningTriangleIcon } from 'src/assets/svg/warning.svg'
import { Skeleton } from 'src/components/Skeleton'
import {
  PlatformAnalysis,
  RatingMismatchReview,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './ratingAnalytics.module.scss'
import { RatingMismatchModal } from '../../../RatingMismatchModal'

// All three cards in .cardsRow are a fixed 152px tall (ratingAnalytics.module
// .scss), so this section cannot collapse or shift. The skeletons exist because
// the DEFAULT_DISTRIBUTION / DEFAULT_TREND fallbacks below are invented numbers:
// while the query was in flight the widget drew a complete, confident-looking
// 48/32/12/5/3 distribution and a rising trend line that were pure fiction.
// .bigRatingValue is 36px, .distPercent 12px, .distStars min-width 78px.
const BIG_RATING_SKELETON = { height: 36, width: 72 }
const DIST_BAR_SKELETON = { height: 8, width: 86 }
const DIST_PERCENT_SKELETON = { height: 12, width: 30 }
const DIST_ROW_COUNT = 5

interface RatingAnalyticsProps {
  ProviderIcon?: React.FC<React.SVGProps<SVGSVGElement>> | null
  averageRating?: number
  averageRatingChangePercent?: number
  averageRatingDelta?: number
  distribution?: { percentage: number; stars: number }[]
  isLoading?: boolean
  mismatchCount?: number
  mismatchPercentage?: number
  mismatchReviews?: RatingMismatchReview[]
  providerName?: string
  trend?: PlatformAnalysis['trend']
}

const DEFAULT_DISTRIBUTION = [
  { percentage: 48, stars: 5 },
  { percentage: 32, stars: 4 },
  { percentage: 12, stars: 3 },
  { percentage: 5, stars: 2 },
  { percentage: 3, stars: 1 },
]

const DEFAULT_TREND: PlatformAnalysis['trend'] = {
  barHeights: [40, 55, 30, 65, 50, 70, 45, 60, 35, 75, 55, 80],
  ratingSeries: [3, 3.4, 3.1, 3.6, 3.4, 3.9, 3.6, 3.8, 3.5, 4.2, 4, 4.4],
  sentimentSeries: [55, 60, 57, 66, 62, 70, 66, 70, 64, 78, 74, 82],
  xAxisFrom: '2026 Jan 17',
  xAxisTo: '2026 Feb 17',
}

const fmtRating = (n: number) => n.toFixed(1).replace('.', ',')

// series value -> SVG points in a 0..100 box (x across buckets, y via toY).
// Empty buckets (null) are dropped so the line only spans weeks with data.
const pointsFrom = (
  series: (number | null)[] | undefined,
  toY: (v: number) => number,
  count: number
) =>
  (series ?? [])
    .map((v, i) =>
      v == null
        ? null
        : `${((count > 1 ? i / (count - 1) : 0.5) * 100).toFixed(1)},${toY(
            v
          ).toFixed(1)}`
    )
    .filter(Boolean)
    .join(' ')

export const RatingAnalytics: FC<RatingAnalyticsProps> = ({
  averageRating,
  averageRatingChangePercent,
  averageRatingDelta,
  distribution: distributionProp,
  isLoading = false,
  mismatchCount,
  mismatchPercentage,
  mismatchReviews = [],
  providerName = 'Tripadvisor',
  ProviderIcon = TripadvisorIcon,
  trend: trendProp,
}) => {
  const [showMismatch, setShowMismatch] = useState(false)

  // First load only: the two chart props are undefined both while the query is
  // in flight and before it has ever run. Once real values arrive a refetch
  // keeps them on screen.
  const isInitialLoading = isLoading && !distributionProp && !trendProp

  const distribution = distributionProp ?? DEFAULT_DISTRIBUTION
  const trend = trendProp ?? DEFAULT_TREND
  const buckets = trend.barHeights.length
  // rating 1-5 → y (5 at top); sentiment % onto the same axis (100%↔top).
  const ratingPoints = pointsFrom(
    trend.ratingSeries,
    (v) => ((5 - v) / 4) * 100,
    buckets
  )
  const sentimentPoints = pointsFrom(
    trend.sentimentSeries,
    (v) => 100 - v,
    buckets
  )
  const ratingUp = (averageRatingChangePercent ?? 0) >= 0
  return (
    <div className={styles.container}>
      <h3 className={styles.header}>Rating Analytics</h3>

      <div className={styles.cardsRow}>
        {/* Average Rating Card */}
        <div className={styles.avgRatingCard}>
          <div className={styles.cardTopRow}>
            <div className={styles.cardTitleBlock}>
              <p className={styles.cardTitle}>Average Rating</p>
              <p className={styles.cardSubtitle}>Last 30 days</p>
            </div>
            <div className={styles.providerBadge}>
              {ProviderIcon && (
                <div className={styles.providerIcon}>
                  <ProviderIcon />
                </div>
              )}
              <span className={styles.providerName}>{providerName}</span>
            </div>
          </div>
          <div className={styles.cardBottomRow}>
            <div className={styles.ratingDisplay}>
              <div className={styles.bigStar}>
                <StarIcon />
              </div>
              {isInitialLoading ? (
                <Skeleton
                  height={BIG_RATING_SKELETON.height}
                  width={BIG_RATING_SKELETON.width}
                />
              ) : (
                <p className={styles.bigRatingValue}>
                  {averageRating != null ? fmtRating(averageRating) : '—'}
                </p>
              )}
            </div>
            {!isInitialLoading && averageRatingChangePercent != null && (
              <div className={styles.trendSection}>
                <div className={styles.trendBadge}>
                  <div className={styles.trendBadgeIcon}>
                    <TrendUpIcon
                      style={{
                        color: ratingUp ? '#10B981' : '#F97316',
                        transform: ratingUp ? undefined : 'rotate(180deg)',
                      }}
                    />
                  </div>
                  <span className={styles.trendBadgeValue}>
                    {Math.abs(averageRatingChangePercent)}%
                  </span>
                </div>
                {averageRatingDelta != null && (
                  <span className={styles.trendLabel}>
                    {averageRatingDelta >= 0 ? '+' : ''}
                    {fmtRating(averageRatingDelta)} vs previous period
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Rating Distribution Card */}
        <div className={styles.distributionCard}>
          <div className={styles.distHeader}>
            <p className={styles.distTitle}>Rating Distribution</p>
            <div className={styles.providerBadge}>
              {ProviderIcon && (
                <div className={styles.providerIcon}>
                  <ProviderIcon />
                </div>
              )}
              <span className={styles.providerName}>{providerName}</span>
            </div>
          </div>
          <div className={styles.distBody}>
            {/* The 5→1 star labels are structure, not data, so they stay put;
                only the bar and the percentage are unknown while loading. */}
            {isInitialLoading &&
              Array.from({ length: DIST_ROW_COUNT }).map((_, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={styles.distRow} key={idx}>
                  <div className={styles.distStars}>
                    {Array.from({ length: DIST_ROW_COUNT - idx }).map(
                      (__, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div className={styles.distStar} key={i}>
                          <StarIcon />
                        </div>
                      )
                    )}
                  </div>
                  <Skeleton
                    height={DIST_BAR_SKELETON.height}
                    radius={4}
                    width={DIST_BAR_SKELETON.width}
                  />
                  <Skeleton
                    height={DIST_PERCENT_SKELETON.height}
                    width={DIST_PERCENT_SKELETON.width}
                  />
                </div>
              ))}
            {!isInitialLoading &&
              distribution.map((row) => (
                <div className={styles.distRow} key={row.stars}>
                  <div className={styles.distStars}>
                    {Array.from({ length: row.stars }).map((_, i) => (
                      <div className={styles.distStar} key={i}>
                        <StarIcon />
                      </div>
                    ))}
                  </div>
                  <div
                    className={styles.distBar}
                    style={{ width: `${row.percentage * 1.8}px` }}
                  />
                  <span className={styles.distPercent}>{row.percentage}%</span>
                </div>
              ))}
          </div>
        </div>

        {/* Trend Card */}
        <div className={styles.trendCard}>
          <div className={styles.trendCardHeader}>
            <div className={styles.trendCardTitleRow}>
              <p className={styles.trendCardTitle}>Trend</p>
              {ProviderIcon && (
                <div className={styles.trendCardTitleIcon}>
                  <ProviderIcon />
                </div>
              )}
            </div>
            <div className={styles.trendLegend}>
              <div className={styles.legendItem}>
                <span
                  className={`${styles.legendDot} ${styles.legendDotRating}`}
                />
                Rating
              </div>
              <div className={styles.legendItem}>
                <span
                  className={`${styles.legendDot} ${styles.legendDotSentiment}`}
                />
                Sentiment
              </div>
            </div>
          </div>
          <div className={styles.trendChartArea}>
            <div className={styles.chartYAxis}>
              {[5, 4, 3, 2, 1].map((n) => (
                <span className={styles.chartYLabel} key={n}>
                  {n}
                </span>
              ))}
            </div>
            <div className={styles.chartBody}>
              {/* Bars keep their real container and are simply flat while
                  loading — DEFAULT_TREND's shape would otherwise read as a
                  genuine 12-week history. */}
              <div className={styles.chartBars}>
                {trend.barHeights.map((h, i) => (
                  <div
                    className={styles.chartBar}
                    key={i}
                    style={{ height: isInitialLoading ? '0%' : `${h}%` }}
                  />
                ))}
              </div>
              <svg
                className={styles.chartLines}
                preserveAspectRatio="none"
                viewBox="0 0 100 100"
              >
                {/* Rating line (orange) — avg rating per week */}
                {!isInitialLoading && ratingPoints && (
                  <polyline
                    fill="none"
                    points={ratingPoints}
                    stroke="#F97316"
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {/* Sentiment line (blue) — positive % per week */}
                {!isInitialLoading && sentimentPoints && (
                  <polyline
                    fill="none"
                    points={sentimentPoints}
                    stroke="#3B82F6"
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
              </svg>
            </div>
          </div>
          <div className={styles.chartXAxis}>
            {isInitialLoading ? (
              <>
                <Skeleton height={10} width={64} />
                <Skeleton height={10} width={64} />
              </>
            ) : (
              <>
                <span className={styles.chartXLabel}>{trend.xAxisFrom}</span>
                <span className={styles.chartXLabel}>{trend.xAxisTo}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className={styles.warningBanner}>
        <div className={styles.warningIconContainer}>
          <div className={styles.warningIcon}>
            <InfoIcon />
          </div>
        </div>
        <div className={styles.warningContent}>
          <p className={styles.warningTitle}>Rating vs Sentiment Mismatch</p>
          <div className={styles.warningMeta}>
            <div className={styles.warningMetaItem}>
              <div className={styles.warningMetaStar}>
                <StarIcon />
              </div>
              {isInitialLoading ? (
                <Skeleton height={16} width={24} />
              ) : (
                <strong>{mismatchCount ?? 0}</strong>
              )}
              <span>reviews with Negative Text</span>
            </div>
            <div className={styles.warningMetaItem}>
              <div className={styles.warningTriangle}>
                <WarningTriangleIcon className={styles.warningTriangleIcon} />
              </div>
              {isInitialLoading ? (
                <Skeleton height={16} width={36} />
              ) : (
                <strong>{mismatchPercentage ?? 0}%</strong>
              )}
            </div>
          </div>
        </div>
        <button
          className={styles.viewDetailsBtn}
          disabled={mismatchReviews.length === 0}
          onClick={() => setShowMismatch(true)}
          type="button"
        >
          View Details
        </button>
      </div>

      <RatingMismatchModal
        isOpen={showMismatch}
        onClose={() => setShowMismatch(false)}
        reviews={mismatchReviews}
      />
    </div>
  )
}
